import { Link, useParams } from "react-router-dom";
import { ArrowRight, MessageCircle, PackageCheck, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ProductCard } from "../components/ProductCard";
import { TextileVisual } from "../components/TextileVisual";
import { categoryRoutes } from "../data/siteContent";

export function ShopPage({ products, categories, onAddToCart, onWhatsAppOrder }) {
  return (
    <section className="page-section section-pad" data-testid="shop-page">
      <div className="page-intro">
        <p className="section-kicker" data-testid="shop-kicker">Shop Project Umeed</p>
        <h1 data-testid="shop-heading">Premium handmade products that carry craft, culture and access.</h1>
        <p data-testid="shop-copy">Explore textile-led tote bags, pouches, keychains and pencil boxes handcrafted by women artisans from Kutch.</p>
      </div>
      <div className="category-pills" data-testid="shop-category-pills">
        {categories.map((category) => (
          <Link to={`/category/${category.slug}`} key={category.slug} data-testid={`shop-category-pill-${category.slug}`}>{category.name}</Link>
        ))}
      </div>
      <div className="product-grid" data-testid="shop-products-grid">
        {products.map((product) => <ProductCard key={product.slug} product={product} onAddToCart={onAddToCart} onWhatsAppOrder={onWhatsAppOrder} />)}
      </div>
    </section>
  );
}

export function CategoryPage({ products, categories, onAddToCart, onWhatsAppOrder }) {
  const { slug } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const categoryProducts = products.filter((product) => product.category_slug === slug);
  const title = category?.name || categoryRoutes[slug] || "Collection";

  return (
    <section className="page-section section-pad" data-testid={`category-page-${slug}`}>
      <div className="category-hero">
        <div>
          <p className="section-kicker" data-testid="category-page-kicker">Project Umeed Collection</p>
          <h1 data-testid="category-page-heading">{title}</h1>
          <p data-testid="category-page-copy">{category?.description || "Handcrafted textile products made by women artisans from Kutch."}</p>
          <Badge className="umeed-badge" data-testid="category-page-story-tag">{category?.story_tag || "Handcrafted in Kutch"}</Badge>
        </div>
        <TextileVisual visualKey={category?.visual_key || "tote"} label={`${title} textile visual`} testId="category-page-visual" />
      </div>
      <div className="product-grid" data-testid="category-products-grid">
        {categoryProducts.map((product) => <ProductCard key={product.slug} product={product} onAddToCart={onAddToCart} onWhatsAppOrder={onWhatsAppOrder} />)}
      </div>
    </section>
  );
}

export function ProductPage({ products, onAddToCart, onWhatsAppOrder }) {
  const { slug } = useParams();
  const product = products.find((item) => item.slug === slug);
  if (!product) {
    return (
      <section className="page-section section-pad" data-testid="product-not-found-page">
        <h1 data-testid="product-not-found-heading">Product not found</h1>
        <Button className="btn-primary" asChild data-testid="product-not-found-shop-link"><Link to="/shop">Return to Shop</Link></Button>
      </section>
    );
  }
  const related = products.filter((item) => product.related_slugs?.includes(item.slug));

  return (
    <section className="product-page section-pad" data-testid={`product-page-${product.slug}`}>
      <div className="product-detail">
        <TextileVisual visualKey={product.visual_key} label={`${product.name} textile visual`} testId="product-detail-visual" />
        <div className="product-detail__copy">
          <Badge className="umeed-badge" data-testid="product-detail-story-tag">{product.story_tag}</Badge>
          <h1 data-testid="product-detail-name">{product.name}</h1>
          <p className="product-detail__lead" data-testid="product-detail-emotional-description">{product.emotional_description}</p>
          <strong className="product-detail__price" data-testid="product-detail-price">₹{product.price.toLocaleString("en-IN")}</strong>
          <div className="product-detail__actions">
            <Button className="btn-primary" onClick={() => onAddToCart(product)} data-testid="product-detail-add-to-cart-button"><ShoppingBag aria-hidden="true" /> Add to Cart</Button>
            <Button className="btn-quiet" variant="outline" onClick={() => onWhatsAppOrder(product)} data-testid="product-detail-whatsapp-button"><MessageCircle aria-hidden="true" /> WhatsApp Order</Button>
            {product.stock_status === "Pre-order" && <Button className="btn-quiet" variant="outline" onClick={() => onAddToCart(product)} data-testid="product-detail-preorder-button"><PackageCheck aria-hidden="true" /> Pre-order</Button>}
          </div>
          <div className="product-facts" data-testid="product-facts-grid">
            <Fact title="Practical use" value={product.practical_use_case} id="practical-use" />
            <Fact title="Material details" value={product.material_details} id="material-details" />
            <Fact title="Sustainability note" value={product.sustainability_note} id="sustainability-note" />
            <Fact title="Artisan story" value={product.artisan_story} id="artisan-story" />
            <Fact title="Care instructions" value={product.care_instructions} id="care-instructions" />
            <Fact title="Gifting suitability" value={product.gifting_suitability} id="gifting-suitability" />
          </div>
        </div>
      </div>
      {related.length > 0 && (
        <div className="related-products" data-testid="related-products-section">
          <div className="section-heading-row">
            <h2 data-testid="related-products-heading">Related products</h2>
            <Link to="/shop" data-testid="related-products-shop-link">Shop all <ArrowRight aria-hidden="true" /></Link>
          </div>
          <div className="product-grid" data-testid="related-products-grid">
            {related.map((item) => <ProductCard key={item.slug} product={item} onAddToCart={onAddToCart} onWhatsAppOrder={onWhatsAppOrder} />)}
          </div>
        </div>
      )}
    </section>
  );
}

function Fact({ title, value, id }) {
  return (
    <div className="product-fact" data-testid={`product-fact-${id}`}>
      <span data-testid={`product-fact-title-${id}`}>{title}</span>
      <p data-testid={`product-fact-value-${id}`}>{value}</p>
    </div>
  );
}